import {
  Campaign,
  ChevronRight,
  ExpandMore,
  InfoOutlined,
  ArrowBackIosNew,
  Vibration,
  VolumeUp,
} from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Divider,
  IconButton,
  Link,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { motion } from 'framer-motion';
import React from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  channelSupport,
  glossary,
  guideTopIntro,
  iosBrief,
  oemGuides,
  whyManualSettings,
} from './guideData';

const MotionBox = motion.create(Box);

const publicAsset = (path: string) => {
  const base = (process.env.PUBLIC_URL || '').replace(/\/$/, '');
  return `${base}${path.startsWith('/') ? path : `/${path}`}`;
};

const XIAOMI_REF_IMAGES = [
  {
    src: publicAsset('/static/notification-guide/xiaomi_push_01.jpg'),
    alt: '在系统设置中找到飞舟的通知入口（界面以本机为准）',
    caption: '找到飞舟通知入口',
  },
  {
    src: publicAsset('/static/notification-guide/xiaomi_push_02.jpg'),
    alt: '按通知类别开启横幅、铃声、振动等（其他品牌路径类似）',
    caption: '按类别开横幅 / 铃声 / 振动',
  },
] as const;

const sectionMotion = {
  initial: { opacity: 0, y: 16 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: '-40px' },
  transition: { duration: 0.45, ease: [0.22, 1, 0.36, 1] as const },
};

const glassCardSx = {
  borderRadius: 3,
  background: alpha('#ffffff', 0.08),
  border: `1px solid ${alpha('#ffffff', 0.14)}`,
  backdropFilter: 'blur(12px)',
  color: 'rgba(248,250,252,0.96)',
};

const AndroidNotificationGuidePage: React.FC = () => {
  const navigate = useNavigate();

  return (
    <Box
      sx={{
        minHeight: '100vh',
        pb: 'calc(24px + env(safe-area-inset-bottom, 0px))',
        pt: 'env(safe-area-inset-top, 0px)',
        background: `
          radial-gradient(1200px 600px at 10% -10%, ${alpha('#2dd4bf', 0.35)}, transparent 55%),
          radial-gradient(900px 500px at 110% 20%, ${alpha('#6366f1', 0.35)}, transparent 50%),
          linear-gradient(165deg, #0b1220 0%, #0f172a 38%, #0c4a6e 72%, #042f2e 100%)
        `,
        color: '#e2e8f0',
      }}
    >
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 10,
          borderBottom: `1px solid ${alpha('#fff', 0.08)}`,
          background: alpha('#0f172a', 0.72),
          backdropFilter: 'blur(14px)',
        }}
      >
        <Container maxWidth="md" sx={{ py: 1.25, display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton
            onClick={() => navigate(-1)}
            sx={{ color: '#f1f5f9', mr: 0.5 }}
            aria-label="返回"
            size="small"
          >
            <ArrowBackIosNew sx={{ fontSize: 18 }} />
          </IconButton>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, flex: 1, color: '#f8fafc' }}>
            Android 业务通知设置指南
          </Typography>
          <Button
            component={RouterLink}
            to="/"
            size="small"
            sx={{ color: alpha('#fff', 0.85), textTransform: 'none' }}
          >
            官网首页
          </Button>
        </Container>
      </Box>

      <Container maxWidth="md" sx={{ px: 2, pt: 2.5 }}>
        <MotionBox {...sectionMotion} sx={{ mb: 2 }}>
          <Typography
            variant="h5"
            component="h1"
            sx={{
              fontWeight: 800,
              letterSpacing: '-0.02em',
              color: '#f8fafc',
              lineHeight: 1.25,
              fontSize: { xs: '1.35rem', sm: '1.5rem' },
              mb: 1.5,
            }}
          >
            按手机品牌操作，及时看到业务提醒
          </Typography>
          <Card sx={{ ...glassCardSx, textAlign: 'left' }}>
            <CardContent sx={{ py: 2, px: 2.25, '&:last-child': { pb: 2 } }}>
              <Stack spacing={1.5}>
                <Typography variant="body2" sx={{ color: alpha('#e2e8f0', 0.96), lineHeight: 1.75 }}>
                  {guideTopIntro.lead}
                </Typography>
                <Typography variant="body2" sx={{ color: alpha('#e2e8f0', 0.96), lineHeight: 1.75 }}>
                  {guideTopIntro.standard}
                </Typography>
                <Typography variant="body2" sx={{ color: alpha('#e2e8f0', 0.96), lineHeight: 1.75 }}>
                  {guideTopIntro.action}
                </Typography>
              </Stack>
            </CardContent>
          </Card>
        </MotionBox>

        <MotionBox {...sectionMotion} sx={{ mb: 2 }}>
          <Alert
            severity="info"
            icon={<InfoOutlined />}
            sx={{
              py: 0.75,
              ...glassCardSx,
              '& .MuiAlert-icon': { color: '#7dd3fc' },
              '& .MuiAlert-message': { color: alpha('#f1f5f9', 0.95) },
            }}
          >
            菜单名称因系统版本可能不同，可在设置顶部<strong>搜索「飞舟」或「通知」</strong>。
          </Alert>
        </MotionBox>

        {/* 各厂商操作步骤（主体；小米内含并排示意图） */}
        <MotionBox {...sectionMotion} sx={{ mb: 2.5 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#f8fafc', mb: 1.5 }}>
            各手机品牌 · 操作步骤
          </Typography>
          <Stack spacing={1}>
            {oemGuides.map((oem, index) => (
              <Accordion
                key={oem.id}
                disableGutters
                elevation={0}
                defaultExpanded={index === 0}
                sx={{
                  ...glassCardSx,
                  '&:before': { display: 'none' },
                  overflow: 'hidden',
                }}
              >
                <AccordionSummary expandIcon={<ExpandMore sx={{ color: alpha('#fff', 0.75) }} />}>
                  <Stack direction="row" spacing={1.5} alignItems="center" sx={{ pr: 1 }}>
                    <Box
                      sx={{
                        width: 10,
                        height: 10,
                        borderRadius: '50%',
                        bgcolor: oem.accent,
                        boxShadow: `0 0 0 4px ${alpha(oem.accent, 0.25)}`,
                      }}
                    />
                    <Box>
                      <Typography sx={{ fontWeight: 700, color: '#f8fafc' }}>{oem.name}</Typography>
                      <Typography variant="caption" sx={{ color: alpha('#cbd5e1', 0.9) }}>
                        {oem.subtitle}
                      </Typography>
                    </Box>
                  </Stack>
                </AccordionSummary>
                <AccordionDetails sx={{ pt: 0, px: 2, pb: 2 }}>
                  <Divider sx={{ borderColor: alpha('#fff', 0.08), mb: 2 }} />
                  {oem.id === 'xiaomi' && (
                    <Box sx={{ mb: 2 }}>
                      <Typography
                        variant="subtitle2"
                        sx={{ fontWeight: 700, color: '#fed7aa', mb: 0.75 }}
                      >
                        参考示意（小米）
                      </Typography>
                      <Typography variant="caption" sx={{ display: 'block', color: alpha('#cbd5e1', 0.95), mb: 1.25, lineHeight: 1.55 }}>
                        以下为小米手机上的界面截图（并排等比展示）。华为、荣耀、OPPO、vivo 等同样在「设置 → 通知」中为飞舟逐类开启即可，仅菜单名称可能略有不同。
                      </Typography>
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'row',
                          alignItems: 'flex-start',
                          gap: 1.25,
                          boxSizing: 'border-box',
                          mb: 0.5,
                          // 手机：占满屏幕宽度（减去左右留白），每张约为 (100vw - margin - gap) / 2
                          width: { xs: '100vw', md: '100%' },
                          maxWidth: { xs: '100vw', md: '100%' },
                          position: 'relative',
                          left: { xs: '50%', md: 'auto' },
                          transform: { xs: 'translateX(-50%)', md: 'none' },
                          px: { xs: 2, md: 0 },
                        }}
                      >
                        {XIAOMI_REF_IMAGES.map((item) => (
                          <Box
                            key={item.src}
                            sx={{
                              flex: { xs: '1 1 0', md: '1 1 0' },
                              minWidth: 0,
                              borderRadius: 1.5,
                              overflow: 'hidden',
                              border: `1px solid ${alpha('#fff', 0.1)}`,
                              bgcolor: alpha('#020617', 0.55),
                              display: 'flex',
                              flexDirection: 'column',
                            }}
                          >
                            <Box
                              component="img"
                              src={item.src}
                              alt={item.alt}
                              sx={{
                                width: '100%',
                                height: 'auto',
                                display: 'block',
                                objectFit: 'contain',
                                bgcolor: alpha('#0f172a', 0.6),
                              }}
                            />
                            <Typography
                              variant="caption"
                              sx={{
                                display: 'block',
                                px: 0.75,
                                py: 0.75,
                                color: alpha('#e2e8f0', 0.9),
                                lineHeight: 1.45,
                                fontSize: { xs: '0.7rem', sm: '0.75rem' },
                                textAlign: 'center',
                              }}
                            >
                              {item.caption}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  )}
                  <Stack spacing={1.5}>
                    {oem.steps.map((step, i) => (
                      <Card
                        key={step.title}
                        variant="outlined"
                        sx={{
                          borderColor: alpha('#fff', 0.1),
                          bgcolor: alpha('#020617', 0.35),
                          borderRadius: 2,
                        }}
                      >
                        <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                          <Stack direction="row" spacing={1.25} alignItems="flex-start">
                            <Chip
                              label={i + 1}
                              size="small"
                              sx={{
                                height: 24,
                                minWidth: 28,
                                fontWeight: 800,
                                bgcolor: alpha(oem.accent, 0.25),
                                color: '#f8fafc',
                                border: `1px solid ${alpha(oem.accent, 0.45)}`,
                              }}
                            />
                            <Box>
                              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#f8fafc', mb: 0.5 }}>
                                {step.title}
                              </Typography>
                              <Typography variant="body2" sx={{ color: alpha('#e2e8f0', 0.9), lineHeight: 1.7 }}>
                                {step.detail}
                              </Typography>
                            </Box>
                          </Stack>
                        </CardContent>
                      </Card>
                    ))}
                  </Stack>
                  {oem.links && oem.links.length > 0 && (
                    <Stack spacing={1} sx={{ mt: 2 }}>
                      <Typography
                        variant="caption"
                        sx={{ color: alpha('#94a3b8', 0.95), textTransform: 'uppercase', letterSpacing: 0.08 }}
                      >
                        官方说明
                      </Typography>
                      {oem.links.map((l) => (
                        <Link
                          key={l.url}
                          href={l.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          underline="hover"
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 0.5,
                            color: '#7dd3fc',
                            fontWeight: 600,
                            fontSize: 14,
                          }}
                        >
                          {l.label}
                          <ChevronRight sx={{ fontSize: 18 }} />
                        </Link>
                      ))}
                    </Stack>
                  )}
                </AccordionDetails>
              </Accordion>
            ))}
          </Stack>
        </MotionBox>

        <MotionBox {...sectionMotion} sx={{ mb: 2 }}>
          <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', color: alpha('#94a3b8', 0.95), px: 1 }}>
            若仍无法收到提醒，请联系飞舟客服，并提供机型、系统版本与通知设置截图。
          </Typography>
        </MotionBox>

        {/* 文末：扩展说明默认折叠 */}
        <MotionBox {...sectionMotion} sx={{ mb: 1 }}>
          <Accordion
            disableGutters
            elevation={0}
            sx={{
              ...glassCardSx,
              '&:before': { display: 'none' },
            }}
          >
            <AccordionSummary expandIcon={<ExpandMore sx={{ color: alpha('#fff', 0.75) }} />}>
              <Typography sx={{ fontWeight: 700, color: '#f8fafc' }}>更多说明（选读）</Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0, px: 2, pb: 2.5 }}>
              <Divider sx={{ borderColor: alpha('#fff', 0.08), mb: 2 }} />

              <Typography variant="body2" sx={{ color: alpha('#e2e8f0', 0.95), lineHeight: 1.75, mb: 2 }}>
                {whyManualSettings}
              </Typography>

              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#f8fafc', mb: 1.25 }}>
                建议为飞舟打开的提醒项
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} sx={{ mb: 3 }}>
                {[
                  { icon: <Campaign />, title: '横幅 / 悬浮', desc: '强提醒，减少漏看' },
                  { icon: <VolumeUp />, title: '铃声', desc: '按渠道可设不同铃声' },
                  { icon: <Vibration />, title: '振动', desc: '静音环境下仍感知' },
                ].map((c) => (
                  <Card key={c.title} sx={{ ...glassCardSx, flex: 1 }}>
                    <CardContent sx={{ py: 1.5 }}>
                      <Stack direction="row" spacing={1.25} alignItems="center">
                        <Box sx={{ color: '#5eead4' }}>{c.icon}</Box>
                        <Box>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#f8fafc' }}>
                            {c.title}
                          </Typography>
                          <Typography variant="caption" sx={{ color: alpha('#cbd5e1', 0.95) }}>
                            {c.desc}
                          </Typography>
                        </Box>
                      </Stack>
                    </CardContent>
                  </Card>
                ))}
              </Stack>

              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#f8fafc', mb: 1.25 }}>
                名词解释
              </Typography>
              <Stack spacing={1.25} sx={{ mb: 3 }}>
                {glossary.map((item) => (
                  <Card key={item.term} sx={{ ...glassCardSx }}>
                    <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                      <Typography variant="caption" sx={{ color: '#5eead4', fontWeight: 700, display: 'block', mb: 0.5 }}>
                        {item.term}
                      </Typography>
                      <Typography variant="body2" sx={{ color: alpha('#e2e8f0', 0.9), lineHeight: 1.7 }}>
                        {item.desc}
                      </Typography>
                    </CardContent>
                  </Card>
                ))}
              </Stack>

              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#f8fafc', mb: 1 }}>
                各机型推送渠道支持情况
              </Typography>
              <Typography variant="caption" sx={{ display: 'block', color: alpha('#94a3b8', 0.98), mb: 1.25, lineHeight: 1.6 }}>
                即使推送通道可用，仍须在系统通知里打开横幅/铃声/振动，否则可能「静默到达」。
              </Typography>
              <TableContainer sx={{ ...glassCardSx, overflow: 'hidden', mb: 3 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ color: '#94a3b8', fontWeight: 700, borderColor: alpha('#fff', 0.08) }}>
                        机型
                      </TableCell>
                      <TableCell sx={{ color: '#94a3b8', fontWeight: 700, borderColor: alpha('#fff', 0.08) }}>
                        个推渠道
                      </TableCell>
                      <TableCell sx={{ color: '#94a3b8', fontWeight: 700, borderColor: alpha('#fff', 0.08) }}>
                        厂商渠道
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {channelSupport.map((row) => (
                      <TableRow key={row.device} hover>
                        <TableCell sx={{ color: '#f8fafc', fontWeight: 600, borderColor: alpha('#fff', 0.06) }}>
                          {row.device}
                        </TableCell>
                        <TableCell sx={{ color: alpha('#e2e8f0', 0.95), borderColor: alpha('#fff', 0.06) }}>
                          {row.getui}
                        </TableCell>
                        <TableCell sx={{ color: alpha('#e2e8f0', 0.95), borderColor: alpha('#fff', 0.06) }}>
                          {row.vendor}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#f8fafc', mb: 1 }}>
                {iosBrief.title}
              </Typography>
              {iosBrief.paragraphs.map((p, idx) => (
                <Typography
                  key={idx}
                  variant="body2"
                  sx={{ color: alpha('#e2e8f0', 0.92), lineHeight: 1.75, mb: idx === iosBrief.paragraphs.length - 1 ? 0 : 1 }}
                >
                  {p}
                </Typography>
              ))}
            </AccordionDetails>
          </Accordion>
        </MotionBox>
      </Container>
    </Box>
  );
};

export default AndroidNotificationGuidePage;
